import { useEffect, useState } from 'react';

/** The Maps JS API (with the places library) is loaded by a script tag in index.html. */
export const isGoogleMapsLoaded = () =>
  typeof window !== 'undefined' && Boolean(window.google?.maps);

// Google calls this global when it rejects the API key (referrer, billing, quota).
let authFailed = false;
if (typeof window !== 'undefined') {
  const previous = window.gm_authFailure;
  window.gm_authFailure = () => {
    authFailed = true;
    previous?.();
  };
}

export const googleMapsAuthFailed = () => authFailed;

let pending = null;

/** Resolves true once window.google.maps exists, or false after the timeout or a key rejection. */
export function waitForGoogleMaps(timeoutMs = 15000) {
  if (authFailed) return Promise.resolve(false);
  if (isGoogleMapsLoaded()) return Promise.resolve(true);
  if (pending) return pending;

  pending = new Promise((resolve) => {
    const startedAt = Date.now();
    const timer = setInterval(() => {
      if (isGoogleMapsLoaded()) {
        clearInterval(timer);
        pending = null;
        resolve(true);
      } else if (authFailed || Date.now() - startedAt > timeoutMs) {
        clearInterval(timer);
        pending = null;
        resolve(false);
      }
    }, 100);
  });

  return pending;
}

/** Re-renders once the Maps API is available. */
export function useGoogleMaps() {
  const [ready, setReady] = useState(isGoogleMapsLoaded);

  useEffect(() => {
    if (ready) return undefined;
    let alive = true;
    waitForGoogleMaps().then((ok) => {
      if (alive && ok) setReady(true);
    });
    return () => {
      alive = false;
    };
  }, [ready]);

  return ready;
}

export const googleMapsDirectionsUrl = (lat, lng) =>
  `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;

export const googleMapsSearchUrl = (query) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
